import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { components } from "./api/schema";
import Devices from "./pages/Devices";
import Login from "./pages/Login";
import Accept, { ACCEPT_PATH, AcceptSignedIn } from "./pages/Accept";
import BreakGlass, { BREAK_GLASS_PATH, BreakGlassBanner } from "./pages/BreakGlass";
import { FORGOT_PASSWORD_PATH, ForgotPassword, RESET_PASSWORD_PATH, ResetPassword } from "./pages/PasswordReset";
import IdentityLink, { IDENTITY_LINK_PATH } from "./pages/IdentityLink";
import Overview from "./pages/Overview";
import { Boundary, PageFailed } from "./shell/Boundary";
import ChangesAndApplies from "./shell/ChangesAndApplies";
import FacetBar from "./shell/FacetBar";
import { MOVED_FROM, movedTo } from "./shell/moved";
import { entryOf, inOrder, ready, waitingHeading, workspaceKeyOf, workspacesFor, type Need, type NavPage } from "./shell/nav";
import ScopePicker from "./shell/ScopePicker";
import { ReportControl } from "./report/ReportControl";
import SearchBox from "./shell/SearchBox";
import Version, { BuildLabel, StaleNotice, useVersionCheck } from "./shell/Version";
import { useNarrow } from "./shell/narrow";
import { DeviceProvider } from "./shell/device";
import { EstateProvider, useEstate } from "./shell/estate";
import { FreshnessProvider } from "./shell/freshness";
import { FocusFrame } from "./shell/focus";
import { ScopeNavLink, ScopeProvider, scopeSearch, useScope } from "./shell/scope";
import { ForRole } from "./shell/ForRole";
import { useOnlyScopeByDefault } from "./shell/onlyScope";
import { BrandFoot, BrandLockup, useBrand } from "./shell/brand";
import { useSession } from "./shell/session";
import { useTheme, type Theme } from "./shell/theme";
import { alwaysShowsVerticals, chooseVerticals, useVerticalsChosen } from "./shell/verticalPreference";
import { CALLBACK_PATH } from "./shell/provider";
import { SIGNED_OUT_PATH } from "./shell/signout";
import SignedOut from "./pages/SignedOut";
import { Loading } from "./ui";
import { initials } from "./initials";
import { Glyph } from "./icons";
import { useSubjectVertical, useTerm, useTermIcon } from "./lexicon";
import { unrecognized } from "./api/openEnum";

/** The venue cutover page, fetched when somebody opens it.
 *
 * A venue is carried over once, and this page carries the approval set, the
 * seed run and the import form — bytes every session that never cuts a venue
 * over was downloading. The device console is a lazy chunk for the same
 * reason (`pages/Devices.tsx`); #201's ceiling is what asked the question.
 */
const CarryOver = lazy(() => import("./pages/CarryOver"));

/** Operate's and Design's pages (#874), each fetched when somebody opens it.
 *
 * They hold what five lazy pages held — the plan, the comparison, the work
 * list, per-unit access and the networks — and each of those was split out
 * for the same arithmetic: a page opened to plan a site, compare it, approve
 * its work or administer a building's units is not a page a session that has
 * just signed in is looking at. The per-unit access page's own measurement
 * says what one of them costs bundled in:
 *
 *     observed 2026-09-23, `npm run build && npm run size`:
 *       origin/main 6302ec3   index-*.js  485.6 kB of 500.0 kB, 14.4 kB left
 *       this branch           index-*.js  486.0 kB of 500.0 kB, 14.0 kB left
 *       the page itself       Pans-*.js     8.5 kB, downloaded when it is opened
 *
 * So each workspace page is a chunk and the pages it holds are inside it.
 * Operate › Devices is not among them: it is the page an operator lives on,
 * eager for #739's reason below. */
const OperateWired = lazy(() => import("./pages/OperateWired"));
const OperateWireless = lazy(() => import("./pages/OperateWireless"));
const OperateAccess = lazy(() => import("./pages/OperateAccess"));
const OperateFirmware = lazy(() => import("./pages/OperateFirmware"));
const FleetMap = lazy(() => import("./pages/FleetMap"));
const DesignGateway = lazy(() => import("./pages/DesignGateway"));
const DesignPhysical = lazy(() => import("./pages/DesignPhysical"));
const DesignWired = lazy(() => import("./pages/DesignWired"));
const DesignVlans = lazy(() => import("./pages/DesignVlans"));
const DesignWireless = lazy(() => import("./pages/DesignWireless"));
const DesignChanges = lazy(() => import("./pages/DesignChanges"));

/** A device's console, in a window of its own (#537).
 *
 * It sits OUTSIDE `Shell`: the window is one terminal and nothing else, so it
 * has no nav, no scope rail and no page chrome. What it does keep is the gate
 * above it — an anonymous caller gets the sign-in page here as anywhere — and
 * both capabilities, which the API checks and this page only draws around.
 * Lazy for the reason the console has always been lazy: the terminal emulator
 * is bytes a session that never opens a console should not download. */
const ConsoleWindow = lazy(() => import("./pages/ConsoleWindow"));

/** The cells page, fetched when somebody opens it: only a superuser ever can
 * (§4.2), so every other session was downloading it for nothing. Split out
 * when the workspace rail (#749) took the main chunk to 499.6 of #201's 500 kB. */
const Cells = lazy(() => import("./pages/Cells"));

/** The estate's hardware (#1938), fetched when a superuser opens it, for the
 * reason `Cells` is: nobody else ever can. */
const EstateHardware = lazy(() => import("./pages/EstateHardware"));

/** The brand review (#2110), fetched when a superuser opens it, for the reason
 * `Cells` is: nobody else ever can. */
const BrandReview = lazy(() => import("./pages/BrandReview"));

/** Every remaining route but three, split together rather than one at a time
 * (#739).
 *
 * The seven split before it were split **one page at a time, each as it landed**
 * — five of them are inside Operate's and Design's pages now (#874) — and each
 * one's comment recorded the same arithmetic: the page was about to spend what
 * was left, so the page was moved out. `Cells` says it plainly — *"split out when the
 * workspace rail (#749) took the main chunk to 499.6 of #201's 500 kB"*. That
 * clears the warning by a hair and hands the next branch the same problem, which
 * is what #739 was filed to stop: **the next page to grow fails a check it did not
 * break**, and the cheapest repair available to whoever hits it is to raise the
 * limit, which is the one repair that makes the budget meaningless.
 *
 * So the rule these ten follow is a property of the route rather than of its size:
 * **it is eager if a session that has just signed in is already looking at it.**
 * That is `Login`, `Overview` at `/`, and `Devices` — the operational page an
 * operator lives on, and the heaviest subtree here, kept eager deliberately
 * because a round trip on the hot path is the cost this budget exists to avoid
 * spending. Everything else is a navigation away and pays one fetch the first
 * time it is opened, which is what the seven above already do.
 *
 *     observed 2026-09-24, npm run build && npm run size, same node_modules:
 *       origin/main 546efdd   index-*.js  499,392 B = 499.4 kB —  0.6 kB left
 *       this branch           index-*.js  419,356 B = 419.4 kB — 80.6 kB left
 *       the nine new chunks                 75,287 B, each fetched when its route opens
 *
 * **`/audit` was not among them then, and is now** (#1264): its route was split with
 * the rest and vite emitted **no chunk for it**, because `devices/DeviceDetail.tsx`
 * imported `AuditList` from `pages/Audit` for the device's own Audit tab, so the page
 * module was eager through `Devices` whatever the route did. `AuditList` is its own
 * module now and the device page imports that, so the page is a chunk like the rest.
 *
 * **Eight days later the chunk was back in the warning band** (#1264), and not because
 * anything a just-signed-in session looks at had grown. `devices/FamilyTables.tsx`,
 * eager through `Devices`, imported a switch's live read from `devices/Topology.tsx`,
 * which made the whole drawing eager for two lazy routes. The read is
 * `devices/switchRead.tsx` now, so the topology rides in its own chunk:
 *
 *     observed 2026-10-02, npm run build && npm run size, same node_modules:
 *       origin/main 80eb988   index-*.js  489,089 B = 489.1 kB — 10.9 kB left (warning)
 *       Topology moved out    index-*.js  445.4 kB — 54.6 kB left
 *       and Audit split       index-*.js  444,405 B = 444.4 kB — 55.6 kB left
 *       left the chunk: Topology 42.3, Audit 4.9, findings 3.1, PortCard 3.0, WaysOut 1.1 kB
 *       eager JS that index.html loads, all files: 578,695 B → 527,063 B
 *
 * Nothing on the device page was split for it: `Devices` stays eager by the rule above,
 * and the device page's Audit tab still renders `AuditList` from the main chunk.
 *
 * The breakdown that chose them is in !622: `react-dom` is 41% of this chunk and
 * every route needs it, so it stays — moving the framework to a second eager
 * chunk would drop the measured number without changing a byte anybody
 * downloads, and `bundle-size.mjs` measures `index-*.js` precisely because that
 * is what every session fetches. */
const Customers = lazy(() => import("./pages/Customers"));
const DeviceModels = lazy(() => import("./pages/DeviceModels"));
const Integrations = lazy(() => import("./pages/Integrations"));
const Orgs = lazy(() => import("./pages/Orgs"));
/** The organization in scope, Admin's (#2050): opened by an owner, rarely. */
const Organization = lazy(() => import("./pages/Organization"));
const Sites = lazy(() => import("./pages/Sites"));
const SitePage = lazy(() => import("./pages/SitePage"));
/** An organization's private chains and brands (#2110): opened by whoever adds one, rarely. */
const Brands = lazy(() => import("./pages/Brands"));
const ClassificationReport = lazy(() => import("./pages/ClassificationReport"));
const Users = lazy(() => import("./pages/Users"));
/** A scope's sign-in method (#820): opened by an owner, rarely, and by nobody
 * else, so it is a navigation away like the pages above. */
const SignInMethods = lazy(() => import("./pages/SignInMethods"));
const ServicePrincipals = lazy(() => import("./pages/ServicePrincipals"));
/** A customer's portals (#485): administered, not visited, so a navigation
 * away like the pages above. */
const Portals = lazy(() => import("./pages/Portals"));
const Stock = lazy(() => import("./pages/Stock"));
/** A person's own sign-ins (ADR-0162; #974), and the provider's return when a
 * signed-in person linked one. Opened from the account menu, rarely. */
const SignIns = lazy(() => import("./pages/SignIns"));
const LinkReturn = lazy(() => import("./pages/SignIns").then((m) => ({ default: m.LinkReturn })));
/** Every change at the caller's scope (#1264): a navigation away, opened from the
 * rail or from a device's own Audit tab link. */
const Audit = lazy(() => import("./pages/Audit"));

export default function App() {
  const { status } = useSession();
  const location = useLocation();
  // Applied here, ahead of the gate, so the sign-in page is in the theme this
  // browser chose and not in the browser's default until the shell mounts (#253).
  const [theme, setTheme] = useTheme();
  // **A break-glass link is its own page, whatever the session** (ADR-0173;
  // #1012): it spends the link and starts a session, so it answers before the
  // gate — including while the session is still being read, because the token
  // in the address must be taken out of it before anything else is drawn.
  if (location.pathname === BREAK_GLASS_PATH) return <BreakGlass />;
  // **So is an identity link** (ADR-0182; #1034), for the same reasons: the
  // token is in the address, and the person spending it may hold no
  // credential at all yet — it is how the first superuser gets one.
  if (location.pathname === IDENTITY_LINK_PATH) return <IdentityLink />;
  // **So is a password reset link** (ADR-0466 decision 4b; #1977), for the
  // break-glass link's reason: the token is in the address and must be taken
  // out of it before anything else is drawn. It starts no session.
  if (location.pathname === RESET_PASSWORD_PATH) return <ResetPassword />;
  if (status === "loading") return <main className="gate"><Loading label="Signing you in" /></main>;
  // A sign-out on its way to end the browser's session at the provider
  // (ADR-0261): Zenith's half is done, and nothing is drawn behind it.
  if (status === "leaving") return <main className="gate"><Loading label="Signing you out" /></main>;
  // **Where the provider sends the browser back after it ended its session
  // there** (ADR-0261 decision 4a), and where a sign-out lands when it could
  // not be reached (decision 5a). Anonymous by construction: the session was
  // ended before the browser left.
  if (status === "anonymous" && location.pathname === SIGNED_OUT_PATH) return <SignedOut />;
  // **The invitation's acceptance is its own anonymous page** (#506): the
  // invitee has no session and no password, so the sign-in page has nothing
  // to offer them. The callback address stays `Login`'s — it is one address
  // for both journeys, and it tells them apart by where the person was
  // headed.
  if (status === "anonymous" && location.pathname === ACCEPT_PATH) return <Accept />;
  // **And a signed-in browser that opens one is told so** (#1375), rather
  // than drawn the shell's *Not found*: the page signs the session out and
  // the same address is then the acceptance above. Nothing about the
  // invitation is asked from there.
  if (location.pathname === ACCEPT_PATH) return <AcceptSignedIn />;
  // The ask for a reset link is an anonymous page beside the sign-in page,
  // which links to it (ADR-0466 decision 4b).
  if (status === "anonymous" && location.pathname === FORGOT_PASSWORD_PATH) return <ForgotPassword />;
  if (status === "anonymous") return <Login />;
  return (
    <ScopeProvider><EstateProvider><DeviceProvider><FreshnessProvider>
      <Routes>
        <Route path="/console/:serial" element={<Suspense fallback={<main className="console-window"><Loading label="Loading the console" /></main>}><ConsoleWindow /></Suspense>} />
        {/* Everything else is the application. Its own routes are inside. */}
        <Route path="*" element={<Shell theme={theme} setTheme={setTheme} />} />
      </Routes>
    </FreshnessProvider></DeviceProvider></EstateProvider></ScopeProvider>
  );
}

/** An address a page answered at before #874, replaced by the address its
 * content is at now, with the query it should carry (`shell/moved.ts`). */
function Moved() {
  const { pathname, search, hash } = useLocation();
  const to = movedTo(pathname, search);
  return to ? <Navigate replace to={{ ...to, hash }} /> : null;
}

/** Two themes on the tokens (ZENITH.md §18); the ids are what the browser remembers. */
const THEMES: { id: Theme; name: string; hint: string }[] = [
  { id: "slate", name: "Slate", hint: "Dark · cyan" },
  { id: "light", name: "Warm", hint: "Light · indigo" },
];

type Role = components["schemas"]["Role"];

/** Best first — the order `RANK` in authz.ts uses for the walk (§6.2). Repeated
 * here rather than exported because the walk is authz.ts's and this is only a
 * caption; the two say the same six words. */
const ROLES: Role[] = ["owner", "admin", "operator", "viewer", "guest_desk", "unit_manager"];

/** What sits under the person's name: the highest role they hold anywhere, and
 * that there are others when their memberships span more than one. The first
 * membership the server listed is not a role summary (#199); the Users page
 * lists them. */
/** The role this person holds at the scope they picked, for a report's scope
 * group (ADR-0518 2a): the membership at the site, else the customer, else the
 * organization, and `superuser` for a superuser. Undefined where none is at
 * that scope itself; a role reached from above it is not guessed. */
function roleAt(me: { user: { superuser: boolean }; memberships: { role: string; scope_id: string }[] } | undefined, at: { orgId?: string; customerId?: string; siteId?: string }): string | undefined {
  if (me?.user.superuser) return "superuser";
  for (const id of [at.siteId, at.customerId, at.orgId]) {
    const m = id ? me?.memberships.find((x) => x.scope_id === id) : undefined;
    if (m) return m.role;
  }
  return undefined;
}

function roleCaption(me: { user: { superuser: boolean }; memberships: { role: string }[] } | undefined): string {
  if (me?.user.superuser) return "Superuser";
  // A role is the named list or any other string (ADR-0511): one this build does not know is held,
  // and shown as unrecognized where it is the only one.
  const held = new Set<string>(me?.memberships.map((m) => m.role));
  const best = ROLES.find((r) => held.has(r));
  if (!best) return held.size > 0 ? unrecognized([...held][0]) : "Member";
  const words = best.replace("_", " ");
  const name = words[0]?.toUpperCase() + words.slice(1);
  return held.size > 1 ? `${name} and ${held.size - 1} more` : name;
}

function Shell({ theme, setTheme }: { theme: Theme; setTheme(t: Theme): void }) {
  const { me, signOut } = useSession();
  const location = useLocation();
  const { scope, setScope } = useScope();
  // A person who can only mean one site, or one customer, starts there (UX-006).
  useOnlyScopeByDefault();
  // The account menu is open *at an address*: opening a page from anywhere
  // closes it behind it, with no effect to run.
  const [accountAt, setAccountAt] = useState<string>();
  const account = accountAt === location.pathname;
  // The brand this person sees, resolved by the server with the session
  // (§18, #389): the shell wears it for as long as they are signed in.
  const brand = me?.brand;
  useBrand(brand);
  // The scope was this person's: the address and the browser both forget it,
  // so whoever signs in next starts from what they can see (#184).
  const leave = () => { setScope({}); void signOut(); };
  // The workspaces this role may open (#749), and the one the address is in.
  // An address whose workspace the role cannot open — Cells typed by hand —
  // shows the first one's pages; the page itself answers as it always has.
  // The Organizations entry is drawn from how many organizations the
  // session's lists hold (#2050); the scope picker beside it has already
  // asked for them, so this asks for nothing.
  const { lists } = useEstate(false);
  const workspaces = workspacesFor(me, undefined, lists?.orgs.length);
  const here = workspaces.find((w) => w.key === workspaceKeyOf(location.pathname)) ?? workspaces[0];
  // Listed in the vertical the screen reads (ADR-0477 6a, #2067); each
  // workspace's entry below is still asked of its pages as declared.
  const vertical = useSubjectVertical();
  const pages = here ? inOrder(here, vertical) : [];
  // A page that is a lexicon thing draws its key's icon in the vertical the
  // screen reads, as its word does (ADR-0484 decision 3a).
  const iconOf = useTermIcon();
  const term = useTerm();
  // Whether this person's screens follow the selected site's vertical: a
  // switch beside the theme, off until they turn it on, and not offered to a
  // person who works inside one customer's estate (ADR-0489 decisions 1a, 2a).
  const verticalsChosen = useVerticalsChosen();
  const offerVerticals = !alwaysShowsVerticals(me);
  const loadingPortals = `Loading ${term("portal", { many: true, lower: true })}`;
  const now = pages.filter((p) => ready(scope, p));
  const waiting = (["customer", "site"] as Need[])
    .map((need) => ({ need, pages: pages.filter((p) => p.needs === need && !ready(scope, p)) }))
    .filter((g) => g.pages.length > 0);
  // On a phone the header is the workspace row and one line of pages, and the
  // build moves into the account menu; the out-of-date notice stays in view
  // (§18, #1132). One look at /version serves both places.
  const narrow = useNarrow();
  const version = useVersionCheck();
  // The one line scrolls sideways, so the page you are on is brought into it:
  // at the far end of a workspace's pages it would otherwise be off-screen.
  const pagesRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const line = pagesRef.current;
    const on = line?.querySelector<HTMLElement>("a.active:not(.brand)");
    if (!narrow || !line || !on) return;
    const l = line.getBoundingClientRect();
    const a = on.getBoundingClientRect();
    if (a.left < l.left) line.scrollLeft += a.left - l.left;
    else if (a.right > l.right) line.scrollLeft += a.right - l.right;
  }, [narrow, location.pathname]);
  // And it says there is more of it: the edge with pages past it fades
  // (UX-018), since a line cut at "Wirel" read as the last page.
  useEffect(() => {
    const line = pagesRef.current;
    if (!narrow || !line) return;
    const mark = () => {
      line.classList.toggle("more-left", line.scrollLeft > 1);
      line.classList.toggle("more-right", line.scrollLeft + line.clientWidth < line.scrollWidth - 1);
    };
    mark();
    line.addEventListener("scroll", mark, { passive: true });
    window.addEventListener("resize", mark);
    return () => {
      line.removeEventListener("scroll", mark);
      window.removeEventListener("resize", mark);
      line.classList.remove("more-left", "more-right");
    };
  }, [narrow, location.pathname, pages.length]);
  const link = (p: NavPage) => (
    <ScopeNavLink key={p.to} to={p.to} end={p.end}><span className="nav-icon"><Glyph icon={"term" in p.icon ? iconOf(p.icon.term) : p.icon} /></span>{p.word ? term(p.word, { many: true }) : p.label}</ScopeNavLink>
  );
  const workspaceLink = (w: (typeof workspaces)[number]) => {
    const on = w.key === here?.key;
    return (
      <Link key={w.key} to={{ pathname: entryOf(w, scope).to, search: scopeSearch(scope) }} className={"ws" + (on ? " on" : "")} aria-current={on ? "true" : undefined}>
        <Glyph icon={w.icon} size={20} />
        <span>{w.label}</span>
      </Link>
    );
  };
  return (
    // The `.app` grid, which also carries the topology's focus view (#1501).
    <FocusFrame>
      {/* The first stop for the keyboard: past the workspaces, the pages and
          the scope, to the page itself (WCAG 2.4.1, UX-017). Hidden until it
          has focus. It moves focus rather than the address, so the router's
          location, and the scope it carries, are left alone. */}
      <a className="skip-link" href="#content" onClick={(e) => { e.preventDefault(); document.getElementById("content")?.focus(); }}>Skip to content</a>
      <div className="nav">
        <nav className="ws-rail" aria-label="Workspaces">
          {workspaces.filter((w) => w.key !== "admin").map(workspaceLink)}
          {/* Administration sits at the foot, just above the person — where
              settings live — rather than among the day's work (#749). */}
          <div className="ws-foot">
            {workspaces.filter((w) => w.key === "admin").map(workspaceLink)}
            <ReportControl roleOf={(at) => roleAt(me, at)} />
            <div className="ws-account">
              <button type="button" className="ws-avatar" aria-expanded={account} aria-controls="account-menu"
                aria-label={`Account: ${me?.user.name || me?.user.email || ""}`} onClick={() => setAccountAt(account ? undefined : location.pathname)}>{initials(me)}</button>
              {account && (
                <div id="account-menu" className="account-menu">
                  <div className="who">
                    <div className="who-name" title={me?.user.email}>{me?.user.name || me?.user.email}</div>
                    <div className="who-role">{roleCaption(me)}</div>
                  </div>
                  <div>
                    <span className="label">Theme</span>
                    <div className="theme-picker" role="radiogroup" aria-label="Theme">
                      {THEMES.map((t) => (
                        <button key={t.id} type="button" role="radio" aria-checked={theme === t.id} className={"btn btn-sm" + (theme === t.id ? " on" : "")} title={t.hint} onClick={() => setTheme(t.id)}>{t.name}</button>
                      ))}
                    </div>
                  </div>
                  {offerVerticals && (
                    <label className="account-check">
                      <input type="checkbox" checked={verticalsChosen} onChange={(e) => chooseVerticals(e.target.checked)} />
                      <span>Show each site in its vertical</span>
                    </label>
                  )}
                  <Link to="/account/sign-ins" className="btn btn-sm" onClick={() => setAccountAt(undefined)}>Your sign-ins</Link>
                  <button type="button" className="btn btn-sm" onClick={leave}>Sign out</button>
                  {/* What the wide navigation carries at its foot (#1132). */}
                  {narrow && (
                    <div className="account-foot">
                      {brand && <BrandFoot brand={brand} />}
                      <BuildLabel server={version.server} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </nav>
        <nav ref={pagesRef} className="nav-column" aria-label={here ? here.label : "Main"}>
          {brand && <ScopeNavLink to="/" className="brand"><BrandLockup brand={brand} /></ScopeNavLink>}
          {here && <div className="nav-title">{here.label}</div>}
          <div className="nav-links">{now.map(link)}</div>
          {/* A page waiting for a level of the scope stays one click away and
              says what it waits for; the page names the same level (#749). */}
          {waiting.map((g) => (
            <div key={g.need} className="nav-waiting">
              <div className="nav-waiting-head">{waitingHeading(g.need)}</div>
              <div className="nav-links">{g.pages.map(link)}</div>
            </div>
          ))}
          {!narrow && (
            <div className="nav-foot">
              {brand && <BrandFoot brand={brand} />}
              <Version check={version} />
            </div>
          )}
        </nav>
        {narrow && version.stale && <div className="nav-stale" role="status"><StaleNotice server={version.server} /></div>}
      </div>
      <div className="main">
        {/* A break-glass session says so on every page (ADR-0173 decision 4a). */}
        {me?.break_glass && <BreakGlassBanner />}
        {/* The scope, then the page's context beside it (§18, #254). */}
        {/* The box beside it finds a device, a customer, a site or a network, and hands an address that is
            no device's to one site's Clients page (§18, ADR-0324 decisions 2 and 3, #1604). */}
        {/* At the right, whether Design holds changes no apply has carried at the
            site in scope, one click from the work list that approves them (#1811), and each apply this
            tab sent, followed from its own record wherever the operator goes (ADR-0480 4a, #2064). */}
        <div className="rail"><ScopePicker /><SearchBox /><FacetBar /><ChangesAndApplies /></div>
        <main className="content" id="content" tabIndex={-1}>
          {/* A page that throws says so here and leaves the shell standing; the
              next navigation forgets the failure (#187). */}
          <Boundary resetKey={location.pathname} fallback={(f) => <PageFailed {...f} />}>
            <Routes>
              <Route path="/" element={<Overview />} />
              <Route path="/orgs" element={<Suspense fallback={<Loading label="Loading organizations" />}><Orgs /></Suspense>} />
              <Route path="/customers" element={<Suspense fallback={<Loading label="Loading customers" />}><Customers /></Suspense>} />
              <Route path="/sites" element={<Suspense fallback={<Loading label="Loading sites" />}><Sites /></Suspense>} />
              {/* A site's own page (#2127): what the list stopped showing, and its acts. */}
              <Route path="/sites/:id" element={<Suspense fallback={<Loading label="Loading the site" />}><SitePage /></Suspense>} />
              {/* Operate reads the network and Design changes it (#874). A
                  page with tabs owns every address under its own. */}
              <Route path="/operate/wired/*" element={<Suspense fallback={<Loading label="Loading the wired side" />}><OperateWired /></Suspense>} />
              <Route path="/operate/devices/*" element={<Devices />} />
              <Route path="/operate/wireless/*" element={<Suspense fallback={<Loading label="Loading wireless" />}><OperateWireless /></Suspense>} />
              <Route path="/operate/access/*" element={<Suspense fallback={<Loading label="Loading access" />}><OperateAccess /></Suspense>} />
              <Route path="/operate/firmware/*" element={<Suspense fallback={<Loading label="Loading firmware" />}><OperateFirmware /></Suspense>} />
              <Route path="/operate/fleet-map" element={<Suspense fallback={<Loading label="Loading the fleet map" />}><FleetMap /></Suspense>} />
              <Route path="/design/physical/*" element={<Suspense fallback={<Loading label="Loading the plan" />}><DesignPhysical /></Suspense>} />
              <Route path="/design/wired/*" element={<Suspense fallback={<Loading label="Loading the cabling" />}><DesignWired /></Suspense>} />
              <Route path="/design/vlans/*" element={<Suspense fallback={<Loading label="Loading the VLANs" />}><DesignVlans /></Suspense>} />
              <Route path="/design/wireless/*" element={<Suspense fallback={<Loading label="Loading networks" />}><DesignWireless /></Suspense>} />
              <Route path="/design/portals" element={<Suspense fallback={<Loading label={loadingPortals} />}><Portals /></Suspense>} />
              <Route path="/design/warehouse" element={<Suspense fallback={<Loading label="Loading the warehouse" />}><Stock /></Suspense>} />
              <Route path="/design/gateway/*" element={<Suspense fallback={<Loading label="Loading the gateway" />}><DesignGateway /></Suspense>} />
              <Route path="/design/changes/*" element={<Suspense fallback={<Loading label="Loading the changes" />}><DesignChanges /></Suspense>} />
              {/* Where the pages #874 moved used to answer: each old address
                  lands on the same content at its new one (`shell/moved.ts`). */}
              {MOVED_FROM.map((from) => <Route key={from} path={from} element={<Moved />} />)}
              <Route path="/carry-over" element={<Suspense fallback={<Loading label="Loading carry-over" />}><CarryOver /></Suspense>} />
              <Route path="/device-models" element={<Suspense fallback={<Loading label="Loading the device catalog" />}><DeviceModels /></Suspense>} />
              <Route path="/organization" element={<ForRole to="/organization"><Suspense fallback={<Loading label="Loading the organization" />}><Organization /></Suspense></ForRole>} />
              <Route path="/integrations" element={<Suspense fallback={<Loading label="Loading integrations" />}><Integrations /></Suspense>} />
              <Route path="/users" element={<ForRole to="/users"><Suspense fallback={<Loading label="Loading users" />}><Users /></Suspense></ForRole>} />
              <Route path="/account/sign-ins" element={<Suspense fallback={<Loading label="Loading your sign-ins" />}><SignIns /></Suspense>} />
              {/* The provider's return reaches the signed-in shell only for a link: a
                  sign-in's return lands on the sign-in page, which a signed-in person never sees. */}
              <Route path={CALLBACK_PATH} element={<Suspense fallback={<Loading label="Finishing the sign-in" />}><LinkReturn /></Suspense>} />
              <Route path="/sign-in-methods" element={<ForRole to="/sign-in-methods"><Suspense fallback={<Loading label="Loading sign-in methods" />}><SignInMethods /></Suspense></ForRole>} />
              <Route path="/service-principals" element={<ForRole to="/service-principals"><Suspense fallback={<Loading label="Loading service principals" />}><ServicePrincipals /></Suspense></ForRole>} />
              <Route path="/audit" element={<ForRole to="/audit"><Suspense fallback={<Loading label="Loading the audit log" />}><Audit /></Suspense></ForRole>} />
              <Route path="/cells/*" element={<Suspense fallback={<Loading label="Loading the cells" />}><Cells /></Suspense>} />
              <Route path="/hardware/*" element={<Suspense fallback={<Loading label="Loading the hardware" />}><EstateHardware /></Suspense>} />
              <Route path="/brands" element={<ForRole to="/brands"><Suspense fallback={<Loading label="Loading the brands" />}><Brands /></Suspense></ForRole>} />
              <Route path="/reports/classification" element={<Suspense fallback={<Loading label="Loading the classification report" />}><ClassificationReport /></Suspense>} />
              <Route path="/brands/review" element={<Suspense fallback={<Loading label="Loading the brand review" />}><BrandReview /></Suspense>} />
              <Route path="*" element={<>
                <div className="page-head"><h1>Not found</h1></div>
                <p>There is nothing at this address. <ScopeNavLink to="/">Back to the overview</ScopeNavLink>.</p>
              </>} />
            </Routes>
          </Boundary>
        </main>
      </div>
    </FocusFrame>
  );
}
